import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Instagram, MapPin, Sparkles, Loader2, Pencil, Save, X, Camera, Images, Eye, EyeOff, Shield } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { EditableText } from "@/components/EditableText";
import { RichText, RichTextEditor, htmlToText } from "@/components/RichText";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/usePageMeta";

const EDIT_FIELDS = ["name", "age", "city", "state", "project_title", "project_description", "project_serves", "project_when", "quote", "instagram"];

export default function WinnerPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [w, setW] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [galleryBusy, setGalleryBusy] = useState(false);

  const toggleGallery = async () => {
    setGalleryBusy(true);
    try {
      const next = !w.gallery_opt_in;
      const { data } = await api.put(`/winners/${slug}`, { gallery_opt_in: next });
      setW((prev) => ({ ...prev, ...data }));
      toast.success(next ? "Your page is now in the Winner Gallery." : "Your page was removed from the Winner Gallery.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update gallery setting.");
    } finally {
      setGalleryBusy(false);
    }
  };

  const startEdit = () => {
    setDraft(Object.fromEntries(EDIT_FIELDS.map((k) => [k, w[k] || ""])));
    setEditing(true);
    toast.info("Edit mode: click any text to change it. Click your photo to swap it.");
  };
  const setD = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const saveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/winners/${slug}`, draft);
      setW((prev) => ({ ...prev, ...data }));
      setEditing(false);
      refresh();
      toast.success("Your page is updated!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/winners/${slug}`);
        setW(data);
        api.get(`/toolkits/${data.toolkit || "bgjcpt"}/settings`).then((r) => setSettings(r.data)).catch(() => {});
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when the slug changes
  }, [slug]);

  const copyLink = () => {
    navigator.clipboard.writeText(w.share_url || window.location.href);
    toast.success("Link copied!");
  };

  usePageMeta(
    w ? {
      title: w.show_name && w.name ? `${w.name} — 2026 #BlackGirlJoyChallenge Winner` : "2026 #BlackGirlJoyChallenge Winner",
      description: htmlToText(w.project_description) || settings?.about_body ||
        "A 2026 #BlackGirlJoyChallenge winner spreading joy with Southern Black Girls and the Pete & Thomas Foundation.",
      image: w.framed_url || "",
      url: w.landing_url,
      type: "profile",
    } : {},
    [w, settings],
  );

  if (loading)
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-sbg-raspberry" />
      </div>
    );

  if (notFound)
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <SiteNav />
        <div className="mx-auto max-w-xl px-5 py-24 text-center">
          <h1 className="font-display text-3xl font-black text-sbg-burgundy">Page not found</h1>
          <p className="mt-3 text-sbg-burgundy/70">This winner page doesn't exist yet.</p>
          <Link to="/bgjcpt" className="mt-6 inline-block rounded-full bg-sbg-fuchsia px-6 py-3 font-bold text-white">
            Make your own
          </Link>
        </div>
        <SiteFooter />
      </div>
    );

  const name = w.show_name && w.name ? w.name : null;
  const location = [w.city, w.state].filter(Boolean).join(", ");
  const isOwner = user && user.winner_slug === slug;
  const canEdit = user && (user.is_admin || isOwner);
  const asAdmin = canEdit && !isOwner;
  const poss = w.name ? `${w.name}'s` : "this winner's";
  const v = (k) => (editing ? draft[k] : w[k]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteNav />

      {editing && (
        <div data-testid="edit-mode-bar" className="sticky top-[65px] z-30 flex flex-wrap items-center justify-between gap-3 bg-sbg-fuchsia px-5 py-3 text-white shadow-lg">
          <span className="text-sm font-bold"><Pencil className="mr-2 inline h-4 w-4" />
            {asAdmin ? <>Editing {poss} page as <span className="rounded bg-white/25 px-1.5 py-0.5">ADMIN · {user.email}</span></> : "Editing your page — click any text to change it"}
          </span>
          <div className="flex gap-2">
            <button data-testid="edit-cancel-btn" onClick={() => setEditing(false)} className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-4 py-2 text-sm font-bold hover:bg-white/30">
              <X className="h-4 w-4" /> Cancel
            </button>
            <button data-testid="edit-save-btn" onClick={saveEdit} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-bold text-sbg-fuchsia disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
            </button>
          </div>
        </div>
      )}

      {asAdmin && !editing && (
        <div data-testid="admin-viewing-bar" className="flex flex-wrap items-center gap-2 bg-pt-purple px-5 py-2 text-xs font-bold text-white">
          <Shield className="h-3.5 w-3.5" /> You are signed in as ADMIN ({user.email}) — viewing {poss} page, not your own.
        </div>
      )}
      {canEdit && !editing && (
        <div data-testid="gallery-toggle-bar" className="flex flex-wrap items-center justify-between gap-3 bg-sbg-blush/60 px-5 py-3">
          <span className="text-sm font-semibold text-sbg-burgundy">
            <Images className="mr-2 inline h-4 w-4 text-sbg-raspberry" />
            {w.gallery_opt_in ? "Your page is showing in the " : "Your page is hidden from the "}
            <Link to="/bgjcpt/gallery" className="font-bold text-sbg-fuchsia underline">Winner Gallery</Link>
          </span>
          <button
            data-testid="gallery-toggle-btn"
            onClick={toggleGallery}
            disabled={galleryBusy}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-transform hover:-translate-y-0.5 disabled:opacity-60 ${w.gallery_opt_in ? "bg-white text-sbg-burgundy ring-1 ring-sbg-raspberry/20" : "bg-sbg-fuchsia text-white"}`}
          >
            {galleryBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : w.gallery_opt_in ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {w.gallery_opt_in ? "Unpublish from gallery" : "Publish to gallery"}
          </button>
        </div>
      )}

      <section className="relative overflow-hidden bg-sbg-burgundy">
        <div className="absolute inset-0 joy-dotgrid opacity-20" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-8 px-5 py-10 lg:grid-cols-2 lg:py-14">
          <div className="animate-fade-up order-2 lg:order-1">
            <span className="inline-flex items-center gap-2 rounded-full bg-sbg-fuchsia px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white">
              <Sparkles className="h-3.5 w-3.5" /> 2026 #BlackGirlJoyChallenge Winner
            </span>
            {editing ? (
              <EditableText editing value={draft.name} onChange={setD("name")} placeholder="Your name" testId="winner-name" className="mt-5 font-display text-3xl font-black" />
            ) : (
              <h1 className="mt-5 font-display text-4xl font-black leading-tight text-white text-balance sm:text-5xl lg:text-6xl">
                {name ? name : "A #BlackGirlJoy Winner"}
              </h1>
            )}
            {editing ? (
              <div className="mt-3 grid grid-cols-3 gap-2">
                <EditableText editing value={draft.age} onChange={setD("age")} placeholder="Age" testId="winner-age" />
                <EditableText editing value={draft.city} onChange={setD("city")} placeholder="City" testId="winner-city" />
                <EditableText editing value={draft.state} onChange={setD("state")} placeholder="State" testId="winner-state" />
              </div>
            ) : (
              <>
                {w.age && (
                  <p className="mt-2 text-lg font-semibold text-sbg-blush/90" data-testid="winner-age">Age {w.age}</p>
                )}
                {location && (
                  <p className="mt-3 inline-flex items-center gap-2 text-lg text-sbg-blush">
                    <MapPin className="h-5 w-5" /> {location}
                  </p>
                )}
              </>
            )}
            <EditableText editing={editing} value={v("project_title")} onChange={setD("project_title")} placeholder="Project title" testId="hero-project-title"
              className={editing ? "mt-5 font-display text-xl font-bold" : "mt-5 max-w-lg font-display text-xl font-bold text-white sm:text-2xl"} />
            {editing && (
              <EditableText editing value={draft.instagram} onChange={setD("instagram")} placeholder="@instagram handle" testId="winner-instagram" className="mt-3" />
            )}
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid="winner-copy-link-btn"
                onClick={copyLink}
                className="inline-flex items-center gap-2 rounded-full bg-sbg-fuchsia px-6 py-3 font-bold text-white transition-transform hover:-translate-y-1"
              >
                <Copy className="h-4 w-4" /> Share this page
              </button>
              {w.instagram && !editing && (
                <a
                  href={`https://instagram.com/${w.instagram.replace("@", "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-white/10 px-6 py-3 font-bold text-white transition-colors hover:bg-white/20"
                >
                  <Instagram className="h-4 w-4" /> {w.instagram}
                </a>
              )}
              {canEdit && !editing && (
                <button
                  onClick={startEdit}
                  data-testid="winner-edit-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-sbg-turquoise px-6 py-3 font-bold text-sbg-burgundy"
                >
                  <Pencil className="h-4 w-4" /> {asAdmin ? `Edit ${poss} page` : "Edit my page"}
                </button>
              )}
            </div>
          </div>

          <div className="animate-pop-in order-1 flex justify-center lg:order-2">
            {w.framed_url ? (
              <div className="relative w-full max-w-md">
                <img
                  src={w.framed_url}
                  alt={name ? `${name}, #BlackGirlJoyChallenge winner` : "#BlackGirlJoyChallenge winner"}
                  className={`w-full rounded-3xl shadow-2xl ${editing ? "cursor-pointer ring-4 ring-sbg-fuchsia ring-offset-4 ring-offset-sbg-burgundy" : ""}`}
                  onClick={() => editing && navigate("/bgjcpt/create?edit=1")}
                  data-testid="winner-framed-image"
                />
                {editing && (
                  <button
                    data-testid="winner-change-photo-btn"
                    onClick={() => navigate("/bgjcpt/create?edit=1")}
                    className="absolute inset-x-0 bottom-4 mx-auto inline-flex w-max items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-sbg-burgundy shadow-lg"
                  >
                    <Camera className="h-4 w-4" /> Change photo or frame
                  </button>
                )}
              </div>
            ) : (
              <div className="flex aspect-square w-full max-w-md items-center justify-center rounded-3xl bg-white/10 text-sbg-blush">
                Photo coming soon
              </div>
            )}
          </div>
        </div>
      </section>

      {/* project details */}
      {(editing || w.project_title || w.project_description || w.project_serves || w.project_when || w.quote) && (
        <section className="mx-auto max-w-5xl px-5 py-12">
          <h2 className="font-display text-sm font-bold uppercase tracking-[0.18em] text-sbg-raspberry">My idea to spread joy in my community…</h2>
          <EditableText editing={editing} value={v("project_title")} onChange={setD("project_title")} placeholder="Project title" testId="project-title"
            className={editing ? "mt-2 font-display text-2xl font-black" : "mt-2 font-display text-3xl font-black text-sbg-burgundy sm:text-4xl"} />
          {editing ? (
            <RichTextEditor testId="project-description-input" value={draft.project_description} onChange={setD("project_description")} placeholder="Describe your project" className="mt-3" />
          ) : (
            <RichText testId="project-description" html={w.project_description} className="mt-3 max-w-3xl text-base leading-relaxed text-sbg-burgundy/80" />
          )}
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {(editing || w.project_serves) && <DetailCard label="Who It Serves" value={v("project_serves")} color="#E02189" editing={editing} onChange={setD("project_serves")} testId="project-serves" />}
            {(editing || w.project_when) && <DetailCard label="When" value={v("project_when")} color="#400057" editing={editing} onChange={setD("project_when")} testId="project-when" />}
            {location && !editing && <DetailCard label="Community" value={location} color="#67DAD7" />}
          </div>
          {(editing || w.quote) && (
            <blockquote className="mt-8 rounded-3xl bg-sbg-blush/50 p-8 text-center">
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-sbg-raspberry" data-testid="quote-heading">
                Why Joy Matters
              </p>
              {editing ? (
                <EditableText editing value={draft.quote} onChange={setD("quote")} placeholder="Why does joy matter to you?" multiline testId="winner-quote" className="font-display text-xl font-bold" />
              ) : (
                <p className="font-display text-2xl font-bold leading-snug text-sbg-burgundy text-balance sm:text-3xl">
                  “{w.quote}”
                </p>
              )}
              {name && !editing && <cite className="mt-4 block text-sm font-bold not-italic text-sbg-raspberry">— {name}</cite>}
            </blockquote>
          )}
        </section>
      )}

      {/* about challenge */}
      <section className="mx-auto max-w-5xl px-5 pb-20">
        <div className="rounded-3xl bg-white p-8 ring-1 ring-sbg-raspberry/10 sm:p-12">
          <h2 className="font-display text-2xl font-bold text-sbg-burgundy sm:text-3xl break-words">{settings?.about_heading || "The #BlackGirlJoyChallenge"}</h2>
          <p className="mt-4 max-w-3xl whitespace-pre-line leading-relaxed text-sbg-burgundy/80">
            {settings?.about_body ||
              "Young people know what their communities need. The #BlackGirlJoyChallenge, an initiative of Southern Black Girls and Women's Consortium, gives them the micro-grants to make it happen. Because joy is not a luxury. It's a strategy, and a powerful tool for creativity, leadership, healing and self-determination. This round of 100 winners is in partnership with Megan Thee Stallion's Pete & Thomas Foundation."}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-6">
            <div>
              <div className="font-display text-4xl font-black text-sbg-fuchsia">1,000+</div>
              <div className="text-sm text-sbg-burgundy/70">Black girls funded since the challenge began</div>
            </div>
            <a
              href="https://www.southernblackgirls.org/"
              target="_blank"
              rel="noreferrer"
              className="rounded-full bg-sbg-burgundy px-6 py-3 font-bold text-white"
            >
              Meet Southern Black Girls
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function DetailCard({ label, value, color, editing, onChange, testId }) {
  return (
    <div className="rounded-2xl bg-white p-6 ring-1 ring-sbg-raspberry/10">
      <div className="text-xs font-bold uppercase tracking-[0.15em]" style={{ color }}>
        {label}
      </div>
      {editing ? (
        <EditableText editing value={value} onChange={onChange} placeholder={label} testId={testId} className="mt-2 text-lg font-semibold" />
      ) : (
        <div className="mt-2 text-lg font-semibold text-sbg-burgundy" data-testid={testId}>{value}</div>
      )}
    </div>
  );
}
